import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/toast';
import useAuthContext from '../../hooks/useAuthContext';
import {
  PaymentMethodDeleteService,
  PaymentMethodsListService,
  PaymentMethodSetDefaultService,
  PaymentMethodUpdateService,
} from './PaymentMethodsService';
import normalizePaymentMethod from './normalizePaymentMethod';

export const paymentMethodsQueryKey = ['payment-methods'];

const queryPaymentMethods = () => {
  const { user, isAuthenticated } = useAuthContext();
  const toast = useToast();
  const queryClient = useQueryClient();

  const list = () => {
    const query = useQuery(
      paymentMethodsQueryKey,
      () => PaymentMethodsListService({ token: user?.access_token }),
      {
        enabled: Boolean(isAuthenticated && user?.access_token),
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5,
      },
    );

    return {
      ...query,
      data: query.data?.data?.data?.map(normalizePaymentMethod) || [],
    };
  };

  const notifyCardAdded = () => {
    toast({
      title: 'Tarjeta agregada',
      status: 'success',
      duration: 2000,
      isClosable: true,
      position: 'top-right',
    });
    queryClient.invalidateQueries(paymentMethodsQueryKey);
  };

  const remove = () => {
    const mutation = useMutation(
      (payload) => PaymentMethodDeleteService({
        token: user?.access_token,
        payment_method_id: payload.payment_method_id,
      }),
      {
        onSuccess: (_, payload) => {
          queryClient.setQueryData(paymentMethodsQueryKey, (old) => {
            const items = old?.data?.data;
            if (!Array.isArray(items)) return old;
            return {
              ...old,
              data: {
                ...old.data,
                data: items.filter((pm) => pm.id !== payload.payment_method_id),
              },
            };
          });
          toast({
            title: 'Tarjeta eliminada',
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
          });
          queryClient.invalidateQueries(paymentMethodsQueryKey);
        },
        onError: () => {
          toast({
            title: 'No se pudo eliminar la tarjeta',
            status: 'error',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
          });
        },
      },
    );

    return mutation;
  };

  const setDefault = () => {
    const mutation = useMutation(
      (payload) => PaymentMethodSetDefaultService({
        token: user?.access_token,
        payment_method_id: payload.payment_method_id,
      }),
      {
        onSuccess: () => {
          toast({
            title: 'Tarjeta principal actualizada',
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
          });
          queryClient.invalidateQueries(paymentMethodsQueryKey);
        },
        onError: () => {
          toast({
            title: 'No se pudo actualizar la tarjeta principal',
            status: 'error',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
          });
        },
      },
    );

    return mutation;
  };

  const update = () => {
    const mutation = useMutation(
      (payload) => PaymentMethodUpdateService({
        token: user?.access_token,
        payment_method_id: payload.payment_method_id,
        holder_name: payload.holder_name,
        exp_month: payload.exp_month,
        exp_year: payload.exp_year,
      }),
      {
        onSuccess: () => {
          toast({
            title: 'Tarjeta actualizada',
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
          });
          queryClient.invalidateQueries(paymentMethodsQueryKey);
        },
        onError: () => {
          toast({
            title: 'No se pudo actualizar la tarjeta',
            status: 'error',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
          });
        },
      },
    );

    return mutation;
  };

  return {
    list,
    notifyCardAdded,
    remove,
    setDefault,
    update,
    paymentMethodsQueryKey,
  };
};

export default queryPaymentMethods;
